/**
 * Allys Area/Neighbourhood Page Skeleton
 *
 * Matches AllysAreaPage: hero, overview, stats, map,
 * properties grid, and area selector.
 * Includes navigation skeleton so the nav bar is always visible.
 */

import { AllysNavSkeleton } from './AllysNavSkeleton';

export function AllysAreaSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <AllysNavSkeleton />

      {/* Area hero */}
      <section className="relative py-28 lg:py-36 overflow-hidden bg-gray-200 skeleton-stagger-1">
        <div className="absolute inset-0 bg-linear-to-b from-[#243462]/40 to-[#243462]/60 skeleton-shimmer" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-3 w-40 rounded bg-white/20 mb-4 skeleton-shimmer" />
          <div className="h-12 w-72 rounded-lg bg-white/20 mb-4 skeleton-shimmer" />
          <div className="h-5 w-96 max-w-full rounded bg-white/10 skeleton-shimmer" />
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 lg:py-24 bg-white skeleton-stagger-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-4">
              <div className="h-8 w-48 rounded-lg bg-gray-200 skeleton-shimmer" />
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-full rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-5/6 rounded bg-gray-100 skeleton-shimmer" />
                <div className="h-4 w-3/4 rounded bg-gray-100 skeleton-shimmer" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="p-6 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="h-8 w-16 rounded bg-gray-200 mb-2 skeleton-shimmer" />
                  <div className="h-4 w-24 rounded bg-gray-100 skeleton-shimmer" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-gray-50 skeleton-stagger-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-6 w-32 rounded bg-gray-200 mb-6 skeleton-shimmer" />
          <div className="h-80 rounded-2xl bg-gray-200 skeleton-shimmer" />
        </div>
      </section>

      {/* Properties in area */}
      <section className="py-16 lg:py-24 bg-white skeleton-stagger-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-8 w-56 rounded-lg bg-gray-200 mb-8 skeleton-shimmer" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-100">
                <div className="aspect-4/3 bg-linear-to-br from-gray-200 to-gray-100 skeleton-shimmer" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-1/2 rounded-full bg-gray-200 skeleton-shimmer" />
                  <div className="h-5 w-3/4 rounded-lg bg-gray-200 skeleton-shimmer" />
                  <div className="h-6 w-1/3 rounded-lg bg-[#243462]/10 skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
